import {
  Button,
  Checkbox,
  Dropdown,
  Field,
  FluentProvider,
  Input,
  Option,
  Text,
  makeStyles,
  tokens,
  webLightTheme,
} from '@fluentui/react-components';
import type {
  IField,
  IFieldsTab,
  IOrderComponentDescriptor,
} from '../../services/metadataService';
import {
  MAX_VISIBILITY_ALLOWLIST_SIZE,
  isOrderComponentSupported,
  supportsVisibilityAllowlist,
} from '@moko/utils';
import { useEffect, useState } from 'react';
import {
  controllerCandidates,
  draftFromField,
  ruleFromDraft,
} from './orderComponentVisibilityDraft';
import VisibilityRulesEditor from './VisibilityRulesEditor';
import { orderComponentFieldName } from './orderComponentFieldName';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
  },
  fullRow: {
    gridColumn: '1 / -1',
  },
  options: {
    display: 'flex',
    flexWrap: 'wrap',
    columnGap: tokens.spacingHorizontalL,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
  },
});

interface OrderComponentSettingsProps {
  field: IField;
  fields: IField[];
  tabs: IFieldsTab[];
  catalog: IOrderComponentDescriptor[];
  catalogLoading: boolean;
  catalogError?: string;
  onSave: (field: IField) => void;
  onCancel: () => void;
}

const OrderComponentSettings = ({
  field,
  fields,
  tabs,
  catalog,
  catalogLoading,
  catalogError,
  onSave,
  onCancel,
}: OrderComponentSettingsProps) => {
  const styles = useStyles();
  const [componentCode, setComponentCode] = useState(field.componentCode ?? '');
  const [label, setLabel] = useState(field.label);
  const [hint, setHint] = useState(field.hint ?? '');
  const [mandatory, setMandatory] = useState(!!field.mandatory);
  const [labelAbove, setLabelAbove] = useState(!!field.labelAbove);
  const [fullWidth, setFullWidth] = useState(!!field.fullWidth);
  const [tabName, setTabName] = useState(field.tabName ?? '');
  const [error, setError] = useState<string>();
  const [visibility, setVisibility] = useState(() =>
    draftFromField(field, fields)
  );
  const selectedComponent = catalog.find((item) => item.code === componentCode);
  const supportsVisibility =
    (selectedComponent?.visibilityOperators ?? []).length > 0;
  const maxAllowlistSize =
    selectedComponent?.maximumAllowlistSize ?? MAX_VISIBILITY_ALLOWLIST_SIZE;
  const supportsAllowlist =
    !!selectedComponent && supportsVisibilityAllowlist(selectedComponent);

  useEffect(() => {
    setComponentCode(field.componentCode ?? '');
    setLabel(field.label);
    setHint(field.hint ?? '');
    setMandatory(!!field.mandatory);
    setLabelAbove(!!field.labelAbove);
    setFullWidth(!!field.fullWidth);
    setTabName(field.tabName ?? '');
    setError(undefined);
  }, [field]);

  const usedByCode = new Map(
    fields
      .filter(
        (candidate) =>
          candidate.fieldType === 'OrderComponent' &&
          candidate.componentCode &&
          candidate.name !== field.name
      )
      .map((candidate) => [
        candidate.componentCode as string,
        candidate.label || candidate.name,
      ])
  );
  const isTypeLocked = Boolean(field.componentCode);

  const save = () => {
    const descriptor = catalog.find((item) => item.code === componentCode);
    if (componentCode && !descriptor) {
      setError('Select a component type from the supported catalogue.');
      return;
    }
    if (descriptor && !isOrderComponentSupported(descriptor)) {
      setError(
        `${descriptor.label} is not supported by this version of the portal.`
      );
      return;
    }
    if (componentCode && usedByCode.has(componentCode)) {
      setError(
        `${
          descriptor?.label ?? componentCode
        } is already used by ${usedByCode.get(componentCode)}.`
      );
      return;
    }
    if (!label.trim()) {
      setError('A label is required.');
      return;
    }

    const visibilityResult = supportsVisibility
      ? ruleFromDraft(field, visibility, fields, maxAllowlistSize)
      : { rule: { dependees: field.dependees, effect: field.effect } };
    if ('error' in visibilityResult) {
      setError(visibilityResult.error);
      return;
    }

    const name = orderComponentFieldName(field, componentCode);
    if (
      fields.some(
        (candidate) =>
          candidate !== field &&
          candidate.name.toLowerCase() === name.toLowerCase()
      )
    ) {
      setError(`Field name ${name} is already in use.`);
      return;
    }
    const allowed = new Set(descriptor?.allowedSettings ?? []);
    onSave({
      name,
      fieldType: 'OrderComponent',
      componentCode: componentCode || undefined,
      label: label.trim(),
      hint: allowed.has('hint') ? hint.trim() || undefined : undefined,
      mandatory: allowed.has('mandatory') ? mandatory : undefined,
      labelAbove: allowed.has('labelAbove') ? labelAbove : undefined,
      fullWidth: allowed.has('fullWidth') ? fullWidth : undefined,
      tabName: allowed.has('tabName') ? tabName || undefined : undefined,
      ...visibilityResult.rule,
      parentName: field.parentName,
    });
  };

  const allowedSettings = new Set(selectedComponent?.allowedSettings ?? []);

  return (
    <FluentProvider theme={webLightTheme} className={styles.root}>
      <Text>
        Configure the component using the application catalogue. Pricing and
        pricing-item identifiers are managed separately.
      </Text>
      <Text>
        API field name: {orderComponentFieldName(field, componentCode)}
      </Text>
      {catalogLoading && <Text role="status">Loading component types…</Text>}
      {catalogError && (
        <Text role="alert" style={{ color: '#a4262c' }}>
          Could not load the supported component types: {catalogError}
        </Text>
      )}
      <div className={styles.grid}>
        <Field label="Component type" required className={styles.fullRow}>
          <Dropdown
            aria-label="Component type"
            placeholder="Select a component type"
            value={selectedComponent?.label ?? ''}
            selectedOptions={componentCode ? [componentCode] : []}
            onOptionSelect={(_event, data) => {
              if (isTypeLocked && data.optionValue !== field.componentCode) {
                return;
              }
              setComponentCode(data.optionValue ?? '');
              setError(undefined);
            }}
          >
            {catalog.map((component) => {
              const usedBy = usedByCode.get(component.code);
              const lockedAlternative =
                isTypeLocked && component.code !== field.componentCode;
              const unsupported = !isOrderComponentSupported(component);
              const disabled = Boolean(
                usedBy || lockedAlternative || unsupported
              );
              const reason = unsupported
                ? 'not supported by this version of the portal'
                : usedBy
                ? `already used by ${usedBy}`
                : lockedAlternative
                ? 'component type is immutable; add a replacement with a new field name'
                : undefined;
              return (
                <Option
                  key={component.code}
                  value={component.code}
                  text={component.label}
                  disabled={disabled}
                >
                  {reason ? `${component.label} (${reason})` : component.label}
                </Option>
              );
            })}
          </Dropdown>
        </Field>
        {isTypeLocked && (
          <Text className={styles.fullRow}>
            Component type is immutable after selection. To replace it, delete
            this field and add a new Order Component using a new field name.
          </Text>
        )}
        <Field label="Field name">
          <Input aria-label="Field name" value={field.name} readOnly />
        </Field>
        {allowedSettings.has('label') && (
          <Field label="Label" required>
            <Input
              aria-label="Label"
              value={label}
              onChange={(_event, data) => setLabel(data.value)}
            />
          </Field>
        )}
        {allowedSettings.has('hint') && (
          <Field label="Hint" className={styles.fullRow}>
            <Input
              aria-label="Hint"
              value={hint}
              onChange={(_event, data) => setHint(data.value)}
            />
          </Field>
        )}
        {allowedSettings.has('tabName') && tabs.length > 0 && (
          <Field label="Tab" className={styles.fullRow}>
            <Dropdown
              aria-label="Tab"
              placeholder="Select a tab"
              value={tabs.find((tab) => tab.tabName === tabName)?.caption ?? ''}
              selectedOptions={tabName ? [tabName] : []}
              onOptionSelect={(_event, data) =>
                setTabName(data.optionValue ?? '')
              }
            >
              {tabs.map((tab) => (
                <Option key={tab.tabName} value={tab.tabName}>
                  {tab.caption}
                </Option>
              ))}
            </Dropdown>
          </Field>
        )}
      </div>
      <div className={styles.options}>
        {allowedSettings.has('mandatory') && (
          <Checkbox
            label="Required"
            checked={mandatory}
            onChange={(_event, data) => setMandatory(!!data.checked)}
          />
        )}
        {allowedSettings.has('labelAbove') && (
          <Checkbox
            label="Show label above the input"
            checked={labelAbove}
            onChange={(_event, data) => setLabelAbove(!!data.checked)}
          />
        )}
        {allowedSettings.has('fullWidth') && (
          <Checkbox
            label="Use full width"
            checked={fullWidth}
            onChange={(_event, data) => setFullWidth(!!data.checked)}
          />
        )}
      </div>
      {supportsVisibility && (
        <VisibilityRulesEditor
          draft={visibility}
          candidates={controllerCandidates(fields, field)}
          allowsAllowlist={supportsAllowlist}
          maxAllowlistSize={maxAllowlistSize}
          onChange={(next) => {
            setVisibility(next);
            setError(undefined);
          }}
        />
      )}
      {error && (
        <Text role="alert" style={{ color: '#a4262c' }}>
          {error}
        </Text>
      )}
      {!componentCode && (
        <Text role="status">
          A component type is required before this Order form can be published.
        </Text>
      )}
      <div className={styles.actions}>
        <Button appearance="primary" onClick={save}>
          Save
        </Button>
        <Button appearance="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </FluentProvider>
  );
};

export default OrderComponentSettings;
